/**
 * AIGenerationModal - Modal for generating AI content with skill targeting.
 *
 * Features:
 * - Discipline, level, and challenge type selection
 * - Skill targeting with multi-select
 * - Count input (1-10 challenges)
 * - Custom instructions textarea
 * - Loading state during generation
 * - Preview of generated challenges with skill tags
 * - Individual accept/reject per challenge
 * - Edit skill tags before saving
 * - Save selected challenges to database
 */

import { useState, useEffect } from "react";
import { Modal } from "../Modal";
import {
  useAIContentGeneration,
  ChallengeTypesByDiscipline,
} from "../../hooks/useAIContentGeneration";
import {
  SkillKeysByDiscipline,
  type DisciplineSlug,
  type SkillKey,
  type ChallengeType,
  type GeneratedChallengeContent,
  type TypedChallengeContent,
} from "@jumpbean/shared";

interface AIGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveComplete?: (count: number) => void;
  /** Pre-select a discipline when opening the modal */
  initialDiscipline?: DisciplineSlug;
  /** Pre-select skills when opening the modal */
  initialSkills?: string[];
}

export function AIGenerationModal({
  isOpen,
  onClose,
  onSaveComplete,
  initialDiscipline,
  initialSkills,
}: AIGenerationModalProps) {
  const {
    formState,
    updateField,
    toggleSkill,
    isGenerating,
    generationError,
    generate,
    generationResult,
    acceptedChallenges,
    toggleChallengeAccepted,
    updateChallengeSkillTags,
    getAcceptedCount,
    isSaving,
    saveError,
    saveAccepted,
    reset,
  } = useAIContentGeneration();

  const [showSkillEditor, setShowSkillEditor] = useState<number | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Apply initial values when modal opens
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (initialDiscipline) {
        updateField("discipline", initialDiscipline);
      }
      if (initialSkills && initialSkills.length > 0) {
        // Add each skill to targetSkills
        for (const skill of initialSkills) {
          toggleSkill(skill as SkillKey);
        }
      }
      setHasInitialized(true);
    }
    // Reset initialization flag when modal closes
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen, initialDiscipline, initialSkills, hasInitialized, updateField, toggleSkill]);

  const handleClose = () => {
    reset();
    setSaveSuccess(false);
    onClose();
  };

  const handleGenerate = async () => {
    setSaveSuccess(false);
    await generate();
  };

  const handleSave = async () => {
    const result = await saveAccepted();
    if (result) {
      setSaveSuccess(true);
      onSaveComplete?.(result.savedCount);
    }
  };

  const handleNewGeneration = () => {
    reset();
    setSaveSuccess(false);
  };

  // Get available challenge types for selected discipline
  const availableChallengeTypes = formState.discipline
    ? ChallengeTypesByDiscipline[formState.discipline]
    : [];

  // Get available skills for selected discipline
  const availableSkills = formState.discipline
    ? SkillKeysByDiscipline[formState.discipline]
    : [];

  const isFormValid = formState.discipline && formState.challengeType;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Generate AI Content"
      size="lg"
      closeOnBackdropClick={!isGenerating && !isSaving}
      closeOnEscape={!isGenerating && !isSaving}
    >
      <div className="space-y-6">
        {/* Success state */}
        {saveSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Challenges saved!</p>
                <p className="text-sm text-green-600">
                  {getAcceptedCount()} challenges have been saved as pending review.
                </p>
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleNewGeneration}
                className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
              >
                Generate More
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Generation form */}
        {!generationResult && !saveSuccess && (
          <div className="space-y-4">
            {/* Discipline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discipline <span className="text-red-500">*</span>
              </label>
              <select
                value={formState.discipline}
                onChange={(e) =>
                  updateField("discipline", e.target.value as DisciplineSlug | "")
                }
                disabled={isGenerating}
                className="
                  w-full px-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm text-gray-700
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <option value="">Select discipline...</option>
                <option value="math">Math (Luna the Unicorn)</option>
                <option value="spelling">Spelling (Petal the Fairy)</option>
                <option value="patterns">Patterns (Prism the Dragon)</option>
                <option value="typing">Typing (Whiskers the Wizard Cat)</option>
                <option value="programming">Programming (Pixel the Robot Owl)</option>
              </select>
            </div>

            {/* Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level <span className="text-red-500">*</span>
              </label>
              <select
                value={formState.level}
                onChange={(e) => updateField("level", parseInt(e.target.value, 10))}
                disabled={isGenerating}
                className="
                  w-full px-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm text-gray-700
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {[1, 2, 3, 4, 5].map((level) => (
                  <option key={level} value={level}>
                    Level {level}
                  </option>
                ))}
              </select>
            </div>

            {/* Challenge Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Challenge Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formState.challengeType}
                onChange={(e) =>
                  updateField("challengeType", e.target.value as ChallengeType | "")
                }
                disabled={isGenerating || !formState.discipline}
                className="
                  w-full px-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm text-gray-700
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <option value="">
                  {formState.discipline
                    ? "Select challenge type..."
                    : "Select discipline first"}
                </option>
                {availableChallengeTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatChallengeType(type)}
                  </option>
                ))}
              </select>
            </div>

            {/* Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Challenges
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={formState.count}
                onChange={(e) =>
                  updateField(
                    "count",
                    Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 1))
                  )
                }
                disabled={isGenerating}
                className="
                  w-full px-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm text-gray-700
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              />
              <p className="mt-1 text-xs text-gray-500">1-10 challenges per generation</p>
            </div>

            {/* Target Skills (optional) */}
            {formState.discipline && availableSkills.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Skills{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select specific skills to focus on, or leave empty for automatic selection.
                </p>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 bg-gray-50 rounded-lg border border-gray-200">
                  {availableSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => toggleSkill(skill)}
                      disabled={isGenerating}
                      className={`
                        px-3 py-1 rounded-full text-xs font-medium
                        transition-colors
                        ${
                          formState.targetSkills.includes(skill)
                            ? "bg-magic-600 text-white"
                            : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
                        }
                        disabled:opacity-50 disabled:cursor-not-allowed
                      `}
                    >
                      {formatSkillName(skill)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Instructions (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Instructions{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={formState.customInstructions}
                onChange={(e) => updateField("customInstructions", e.target.value)}
                disabled={isGenerating}
                maxLength={1000}
                rows={3}
                placeholder="Add any specific instructions for the AI..."
                className="
                  w-full px-4 py-2
                  bg-gray-50 border border-gray-200 rounded-lg
                  text-sm text-gray-700 placeholder-gray-400
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent
                  disabled:opacity-50 disabled:cursor-not-allowed
                  resize-none
                "
              />
              <p className="mt-1 text-xs text-gray-500 text-right">
                {formState.customInstructions.length}/1000
              </p>
            </div>

            {/* Error display */}
            {generationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{generationError}</p>
              </div>
            )}

            {/* Generate button */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isGenerating}
                className="
                  px-4 py-2 text-sm font-medium
                  text-gray-700 bg-gray-100 rounded-lg
                  hover:bg-gray-200 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || !isFormValid}
                className="
                  inline-flex items-center gap-2 px-4 py-2
                  bg-magic-600 text-white rounded-lg
                  hover:bg-magic-700 transition-colors
                  font-medium text-sm
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isGenerating ? (
                  <>
                    <LoadingSpinner className="w-4 h-4" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="w-4 h-4" />
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Results preview */}
        {generationResult && !saveSuccess && (
          <div className="space-y-4">
            {/* Results header */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">
                  Generated {generationResult.challenges.length} Challenges
                </h3>
                <p className="text-sm text-gray-500">
                  {getAcceptedCount()} selected for saving
                </p>
              </div>
              <button
                onClick={handleNewGeneration}
                className="text-sm text-magic-600 hover:text-magic-700 font-medium"
              >
                Start Over
              </button>
            </div>

            {/* Challenge cards */}
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {generationResult.challenges.map((challenge, index) => (
                <ChallengePreviewCard
                  key={index}
                  index={index}
                  challenge={challenge}
                  isAccepted={acceptedChallenges[index] ?? false}
                  onToggleAccepted={() => toggleChallengeAccepted(index)}
                  showSkillEditor={showSkillEditor === index}
                  onToggleSkillEditor={() =>
                    setShowSkillEditor(showSkillEditor === index ? null : index)
                  }
                  availableSkills={availableSkills}
                  onUpdateSkillTags={(skills) => updateChallengeSkillTags(index, skills)}
                  discipline={formState.discipline as DisciplineSlug}
                />
              ))}
            </div>

            {/* Metadata */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
              <p>
                Model: {generationResult.metadata.modelUsed} | Generated:{" "}
                {new Date(generationResult.metadata.generatedAt).toLocaleString()}
              </p>
            </div>

            {/* Save error */}
            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{saveError}</p>
              </div>
            )}

            {/* Save buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSaving}
                className="
                  px-4 py-2 text-sm font-medium
                  text-gray-700 bg-gray-100 rounded-lg
                  hover:bg-gray-200 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || getAcceptedCount() === 0}
                className="
                  inline-flex items-center gap-2 px-4 py-2
                  bg-green-600 text-white rounded-lg
                  hover:bg-green-700 transition-colors
                  font-medium text-sm
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner className="w-4 h-4" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    Save {getAcceptedCount()} Challenges
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ----- Challenge Preview Card -----

interface ChallengePreviewCardProps {
  index: number;
  challenge: GeneratedChallengeContent;
  isAccepted: boolean;
  onToggleAccepted: () => void;
  showSkillEditor: boolean;
  onToggleSkillEditor: () => void;
  availableSkills: readonly SkillKey[];
  onUpdateSkillTags: (skills: SkillKey[]) => void;
  discipline: DisciplineSlug;
}

function ChallengePreviewCard({
  index,
  challenge,
  isAccepted,
  onToggleAccepted,
  showSkillEditor,
  onToggleSkillEditor,
  availableSkills,
  onUpdateSkillTags,
  discipline,
}: ChallengePreviewCardProps) {
  const disciplineColors: Record<string, string> = {
    math: "border-magic-200 bg-magic-50",
    spelling: "border-pink-200 bg-pink-50",
    patterns: "border-purple-200 bg-purple-50",
    typing: "border-blue-200 bg-blue-50",
    programming: "border-green-200 bg-green-50",
  };

  const skillBadgeColors: Record<string, string> = {
    math: "bg-magic-100 text-magic-700",
    spelling: "bg-pink-100 text-pink-700",
    patterns: "bg-purple-100 text-purple-700",
    typing: "bg-blue-100 text-blue-700",
    programming: "bg-green-100 text-green-700",
  };

  const handleSkillToggle = (skill: SkillKey) => {
    const newSkills = challenge.skillTags.includes(skill)
      ? challenge.skillTags.filter((s) => s !== skill)
      : [...challenge.skillTags, skill];
    onUpdateSkillTags(newSkills);
  };

  return (
    <div
      className={`
        border-2 rounded-lg p-4 transition-all
        ${isAccepted ? disciplineColors[discipline] || "border-gray-200 bg-white" : "border-gray-200 bg-gray-50 opacity-60"}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Accept checkbox */}
        <button
          type="button"
          onClick={onToggleAccepted}
          className={`
            flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center
            transition-colors
            ${
              isAccepted
                ? "bg-green-500 border-green-500 text-white"
                : "bg-white border-gray-300 hover:border-gray-400"
            }
          `}
        >
          {isAccepted && <CheckIcon className="w-4 h-4" />}
        </button>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm font-medium text-gray-900">
              Challenge {index + 1}: {formatChallengeType(challenge.type)}
            </span>
            <span className="text-xs text-gray-500">
              Difficulty: {challenge.difficulty}
            </span>
          </div>

          {/* Content preview */}
          <div className="text-sm text-gray-600 mb-3">
            {challenge.promptText || getContentPreview(challenge.content)}
          </div>

          {/* Skill tags */}
          <div className="flex flex-wrap items-center gap-2">
            {challenge.skillTags.map((skill) => (
              <span
                key={skill}
                className={`
                  px-2 py-0.5 rounded-full text-xs font-medium
                  ${skillBadgeColors[discipline] || "bg-gray-100 text-gray-700"}
                `}
              >
                {formatSkillName(skill)}
              </span>
            ))}
            <button
              type="button"
              onClick={onToggleSkillEditor}
              className="text-xs text-magic-600 hover:text-magic-700 font-medium"
            >
              {showSkillEditor ? "Done" : "Edit Skills"}
            </button>
          </div>

          {/* Skill editor */}
          {showSkillEditor && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 mb-2">
                Click skills to add or remove:
              </p>
              <div className="flex flex-wrap gap-1">
                {availableSkills.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => handleSkillToggle(skill)}
                    className={`
                      px-2 py-0.5 rounded-full text-xs font-medium
                      transition-colors
                      ${
                        challenge.skillTags.includes(skill)
                          ? skillBadgeColors[discipline] || "bg-gray-200 text-gray-700"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }
                    `}
                  >
                    {formatSkillName(skill)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ----- Helper Functions -----

function formatChallengeType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatSkillName(skillKey: string): string {
  return skillKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getContentPreview(content: TypedChallengeContent): string {
  // Try to extract a meaningful preview from the content based on type
  const c = content as unknown as Record<string, unknown>;
  if (c.promptText) return String(c.promptText);
  if (c.targetNumber !== undefined)
    return `Find the number ${c.targetNumber}`;
  if (c.targetLetter) return `Find the letter "${c.targetLetter}"`;
  if (c.targetKey) return `Find the "${c.targetKey}" key`;
  if (c.targetWord) return `Type: "${c.targetWord}"`;
  if (c.word) return `What sound does "${c.word}" start with?`;
  if (c.correctAnswer !== undefined && c.operand1 !== undefined) {
    const op1 = c.operand1 as number;
    const op2 = c.operand2 as number;
    if (c.type === "addition") return `${op1} + ${op2} = ?`;
    if (c.type === "subtraction") return `${op1} - ${op2} = ?`;
  }
  if (c.pattern) {
    const pattern = c.pattern as unknown[];
    return `Pattern: ${pattern.slice(0, 4).join(", ")}...`;
  }
  if (c.gridSize) {
    const size = c.gridSize as [number, number];
    return `${size[0]}x${size[1]} maze`;
  }
  return "Challenge content";
}

// ----- Icons -----

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={3}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className || ""}`}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export default AIGenerationModal;
